import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { forum } from "@/lib/api";

export default function ResourcesCommunity() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [form, setForm] = useState({ category: "", title: "", body: "" });
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cats, list] = await Promise.all([forum.categories(), forum.threads()]);
      setCategories(cats);
      setThreads(list);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const m = {};
    for (const t of threads) m[t.category] = (m[t.category] || 0) + 1;
    return m;
  }, [threads]);

  const filtered = useMemo(() => threads.filter((t) => {
    if (activeCategory && t.category !== activeCategory) return false;
    if (search && !`${t.title} ${t.body}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [threads, activeCategory, search]);

  const openComposer = () => {
    setForm({ category: categories[0] || "", title: "", body: "" });
    setError("");
    setComposerOpen(true);
  };

  const submitThread = async (e) => {
    e.preventDefault();
    setError("");
    setPosting(true);
    try {
      const t = await forum.createThread(form);
      navigate(`/resources/community/${t.id}`);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Could not create thread");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="page" data-testid="page-resources-community">
      <div className="page-header">
        <div>
          <div className="page-title">Community Forum</div>
          <div className="page-subtitle">Ask questions, trade notes, and learn from other GovCo.AI users</div>
        </div>
        <button className="btn btn-primary" onClick={openComposer} data-testid="new-thread-btn">+ New Thread</button>
      </div>

      {composerOpen && (
        <div className="card" data-testid="new-thread-panel">
          <div className="card-header"><span className="card-title">Start a new thread</span></div>
          <form onSubmit={submitThread} style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
            <select
              className="select-filter"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="new-thread-category"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              placeholder="Thread title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="new-thread-title"
            />
            <textarea
              placeholder="What do you want to ask or share?"
              rows={4}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="new-thread-body"
            />
            {error && <div className="err" style={{ color: "var(--danger)", fontSize: 12 }}>{error}</div>}
            <div className="flex gap-8">
              <button className="btn btn-primary btn-sm" type="submit" disabled={posting || !form.title.trim() || !form.body.trim()} data-testid="new-thread-submit">
                {posting ? <><span className="spinner white" /> Posting…</> : "Post thread"}
              </button>
              <button className="btn btn-ghost btn-sm" type="button" onClick={() => setComposerOpen(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search threads…" data-testid="community-search" />
        </div>
      </div>

      <div className="community-layout">
        <aside className="community-categories" aria-label="Categories">
          <h2 className="mkt-h3">Categories</h2>
          <div
            className="community-cat"
            style={{ cursor: "pointer", fontWeight: activeCategory === null ? 700 : 400 }}
            onClick={() => setActiveCategory(null)}
            data-testid="category-all"
          >
            <span>All</span>
            <span className="community-cat-count">{threads.length}</span>
          </div>
          {categories.map((c) => (
            <div
              className="community-cat"
              key={c}
              style={{ cursor: "pointer", fontWeight: activeCategory === c ? 700 : 400 }}
              onClick={() => setActiveCategory(c)}
              data-testid={`category-${c}`}
            >
              <span>{c}</span>
              <span className="community-cat-count">{counts[c] || 0}</span>
            </div>
          ))}
        </aside>

        <div className="community-threads">
          <h2 className="mkt-h3">{activeCategory || "Recent threads"} · {filtered.length}</h2>
          {loading ? (
            <div className="center-load"><span className="spinner" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 20, color: "var(--text-secondary)", fontSize: 13 }}>
              No threads yet — be the first to post one.
            </div>
          ) : filtered.map((t) => (
            <div
              className="community-thread"
              key={t.id}
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/resources/community/${t.id}`)}
              data-testid={`thread-${t.id}`}
            >
              <div className="community-thread-main">
                <span className="community-thread-title">{t.title}</span>
                <span className="community-thread-meta">{t.author_name} · {t.category}</span>
              </div>
              <div className="community-thread-replies">{t.reply_count} {t.reply_count === 1 ? "reply" : "replies"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
